import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Zap, Activity } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints';

const BillingSkeleton: React.FC = () => {
  return (
    <Container>
      <DashboardHeader 
        title="Choose Your Plan"
        description="Select the perfect plan for your needs. Plans can be changed anytime."
        icon={<CreditCard size={24} />}
        hideOnMobile={false}
      />
      
      <div className={`grid ${GRID_BREAKPOINTS.billingMain} gap-6`}>
        {/* BILLING SECTION SKELETONS */}
        <div className={`grid ${GRID_BREAKPOINTS.billingSectionSplit} gap-6`}>
          {/* Left Column: Current Plan Card Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="relative rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-purple-500/10 dark:shadow-purple-500/5 h-full overflow-hidden"
          >
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-violet-500/5 dark:from-purple-500/10 dark:to-violet-500/10 rounded-2xl" />
            
            <div className="relative p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <Skeleton className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600" />
                  <div>
                    <Skeleton className="h-6 w-48 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
                    <Skeleton className="h-4 w-32 mt-1 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700" />
                  </div>
                </div>
              </div>
            
            {/* Plan Selection Dropdown Skeleton */}
            <div className="mb-6">
              <div className="relative">
                <Skeleton className="h-10 w-full rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700" />
                {/* Dropdown arrow skeleton */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Skeleton className="w-4 h-4 bg-gray-300 dark:bg-gray-600" />
                </div>
              </div>
            </div>
            
            {/* Feature Breakdown Grid Skeleton */}
            <div className="flex-grow mb-6">
              <div className={`grid ${GRID_BREAKPOINTS.billingFeatureBreakdown} gap-3`}>
                {[...Array(4)].map((_, i) => {
                  // Vary colors like in actual component (blue, green, purple, orange)
                  const colorSchemes = [
                    { bg: 'from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: 'from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40' },
                    { bg: 'from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20', border: 'border-green-200 dark:border-green-800', icon: 'from-green-100 to-green-200 dark:from-green-900/40 dark:to-green-800/40' },
                    { bg: 'from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20', border: 'border-purple-200 dark:border-purple-800', icon: 'from-purple-100 to-purple-200 dark:from-purple-900/40 dark:to-purple-800/40' },
                    { bg: 'from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/20', border: 'border-amber-200 dark:border-amber-800', icon: 'from-amber-100 to-amber-200 dark:from-amber-900/40 dark:to-amber-800/40' }
                  ];
                  const colorScheme = colorSchemes[i % colorSchemes.length];
                  
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: 0.2 + (i * 0.05) }}
                      className={`p-3 rounded-lg border ${colorScheme.border} bg-gradient-to-br ${colorScheme.bg} hover:shadow-md transition-shadow relative overflow-hidden`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <Skeleton className="h-6 w-16 mb-1 bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500" />
                          <Skeleton className="h-4 w-24 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
                        </div>
                        <div className="p-2.5 rounded-lg bg-white/50 dark:bg-gray-800/50">
                          <Skeleton className={`w-6 h-6 rounded bg-gradient-to-br ${colorScheme.icon}`} />
                        </div>
                      </div>
                      
                      {/* Enhanced shimmer effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 dark:via-white/10 to-transparent -translate-x-full"
                        animate={{
                          translateX: ['100%', '200%'],
                          transition: {
                            duration: 2,
                            repeat: Infinity,
                            ease: "linear",
                            delay: i * 0.2
                          }
                        }}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </div>
            </div>
          </motion.div>
          
          {/* Right Column: Credit Transactions Card Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="relative rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 shadow-xl shadow-blue-500/10 dark:shadow-blue-500/5 h-full overflow-hidden"
          >
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 dark:from-blue-500/10 dark:to-blue-500/10 rounded-2xl" />
            
            <div className="relative p-6">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
              <Skeleton className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40" />
              <div className="flex-1">
                <Skeleton className="h-6 w-32 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
              </div>
              <Skeleton className="h-4 w-20 rounded-full bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40" />
            </div>
            
            {/* Transaction List Skeleton */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="space-y-3 px-0 py-2">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 + (i * 0.1) }}
                  className="group relative rounded-lg p-3 transition-all duration-200 hover:bg-white/30 dark:hover:bg-black/20"
                >
                  <div className="relative flex items-center gap-3">
                    {/* Transaction icon */}
                    <Skeleton className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 hidden sm:block flex-shrink-0" />
                    
                    {/* Transaction type badge */}
                    <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                      <Skeleton className="h-6 w-20 rounded-full bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40" />
                    </div>
                    
                    {/* Description */}
                    <div className="flex-1 min-w-0">
                      <Skeleton className="h-4 w-32 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
                    </div>
                    
                    {/* Date */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Skeleton className="h-3 w-3 rounded bg-gray-300 dark:bg-gray-600" />
                      <Skeleton className="h-3 w-12 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700" />
                    </div>
                    
                    {/* Credit amount */}
                    <div className="text-right flex-shrink-0">
                      <Skeleton className="h-4 w-20 bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40" />
                    </div>
                  </div>
                  
                  {/* Transaction shimmer effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 dark:via-white/10 to-transparent -translate-x-full"
                    animate={{
                      translateX: ['100%', '200%'],
                      transition: {
                        duration: 2,
                        repeat: Infinity,
                        ease: "linear",
                        delay: i * 0.3
                      }
                    }}
                  />
                </motion.div>
              ))}
                </div>
              </div>
            
            {/* View Invoices Button Skeleton */}
            <div className="mt-auto pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
              <Skeleton className="h-12 w-full rounded-lg bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40" />
            </div>
            </div>
          </motion.div>
        </div>
        
        {/* USAGE ANALYTICS SECTION SKELETON */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="pt-8 border-t border-gray-200/50 dark:border-gray-700/50"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 
                           flex items-center justify-center shadow-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Usage Analytics
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Track your AI consumption and analyze usage patterns
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* AI Usage Card and Overview Cards Skeletons */}
        <div className={`grid ${GRID_BREAKPOINTS.billingUsageSplit} gap-6 items-stretch`}>
          {/* Left Half: AI Usage Card Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="bg-gradient-to-br from-amber-50/80 via-white/90 to-amber-50/80 dark:from-amber-950/50 dark:via-gray-900/90 dark:to-amber-950/50 rounded-lg p-4 space-y-4 border border-amber-100/50 dark:border-amber-800/30 relative overflow-hidden"
          >
            {/* Progress bar skeleton */}
            <div className="space-y-3">
              <Skeleton className="h-3 w-full rounded-full bg-gradient-to-r from-amber-200 to-orange-200 dark:from-amber-800/50 dark:to-orange-800/50" />
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <Skeleton className="h-4 w-20 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
                </div>
                <Skeleton className="h-4 w-16 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
              </div>
              <div className="flex justify-between items-center text-xs">
                <Skeleton className="h-3 w-24 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700" />
                <Skeleton className="h-3 w-20 bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900/40 dark:to-orange-900/40" />
              </div>
            </div>
            
            {/* Usage card shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/30 dark:via-amber-800/20 to-transparent -translate-x-full"
              animate={{
                translateX: ['100%', '200%'],
                transition: {
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "linear"
                }
              }}
            />
          </motion.div>
          
          {/* Right Half: Overview Cards Skeleton */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            className={`grid ${GRID_BREAKPOINTS.form} gap-6`}
          >
            {[...Array(2)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.6 + (i * 0.1) }}
                className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-sm border-gray-700/50 rounded-lg p-4 h-full relative overflow-hidden border"
              >
                <div className="space-y-3">
                  <Skeleton className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-700 to-purple-700" />
                  <Skeleton className="h-3 w-16 bg-gray-600" />
                  <Skeleton className="h-6 w-12 bg-gray-500" />
                  <Skeleton className="h-3 w-20 bg-gray-600" />
                </div>
                
                {/* Overview card shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full"
                  animate={{
                    translateX: ['100%', '200%'],
                    transition: {
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                      delay: i * 0.4
                    }
                  }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
        
        {/* Analytics Charts Skeleton */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.7 }}
          className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 relative overflow-hidden"
        >
          <div className="flex items-center justify-between mb-6">
            <Skeleton className="h-6 w-32 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
            <div className="flex gap-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-8 w-16 rounded-md bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700" />
              ))}
            </div>
          </div>
          
          {/* Chart area skeleton */}
          <div className="h-64 flex items-end justify-between gap-2">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${Math.random() * 100 + 20}%` }}
                transition={{ duration: 0.5, delay: 0.8 + (i * 0.05) }}
                className="bg-gradient-to-t from-blue-200 to-purple-200 dark:from-blue-800/50 dark:to-purple-800/50 rounded-t-md flex-1 min-h-4"
              />
            ))}
          </div>
          
          {/* Chart shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-white/5 to-transparent -translate-x-full"
            animate={{
              translateX: ['100%', '200%'],
              transition: {
                duration: 3,
                repeat: Infinity,
                ease: "linear",
                delay: 1
              }
            }}
          />
        </motion.div>
        
        {/* Usage Details Table Skeleton */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.8 }}
          className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 relative overflow-hidden"
        >
          <Skeleton className="h-6 w-28 mb-6 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
          
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.9 + (i * 0.05) }}
                className="grid grid-cols-4 gap-4 p-3 rounded-lg bg-gray-50/50 dark:bg-gray-800/50 relative overflow-hidden"
              >
                <Skeleton className="h-4 w-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
                <Skeleton className="h-4 w-3/4 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/40 dark:to-purple-900/40" />
                <Skeleton className="h-4 w-1/2 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40" />
                <Skeleton className="h-4 w-2/3 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700" />
                
                {/* Row shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 dark:via-white/10 to-transparent -translate-x-full col-span-4"
                  animate={{
                    translateX: ['100%', '200%'],
                    transition: {
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                      delay: i * 0.1
                    }
                  }}
                />
              </motion.div>
            ))}
          </div>
          
          {/* Table shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-white/5 to-transparent -translate-x-full"
            animate={{
              translateX: ['100%', '200%'],
              transition: {
                duration: 3,
                repeat: Infinity,
                ease: "linear",
                delay: 1.2
              }
            }}
          />
        </motion.div>
        
        {/* Plans Container Skeleton */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.9 }}
          className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 relative overflow-hidden"
        >
          <Skeleton className="h-6 w-40 mb-6 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 1.0 + (i * 0.1) }}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 relative overflow-hidden"
              >
                <Skeleton className="h-6 w-20 mb-4 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/40 dark:to-blue-900/40" />
                <Skeleton className="h-8 w-16 mb-2 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
                <Skeleton className="h-4 w-24 mb-4 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700" />
                <div className="space-y-2 mb-6">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <Skeleton className="w-4 h-4 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40" />
                      <Skeleton className="h-3 w-24 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700" />
                    </div>
                  ))}
                </div>
                <Skeleton className="h-10 w-full rounded-lg bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/40 dark:to-purple-900/40" />
                
                {/* Plan card shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 dark:via-white/10 to-transparent -translate-x-full"
                  animate={{
                    translateX: ['100%', '200%'],
                    transition: {
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                      delay: i * 0.5
                    }
                  }}
                />
              </motion.div>
            ))}
          </div>
          
          {/* Plans container shimmer effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 dark:via-white/5 to-transparent -translate-x-full"
            animate={{
              translateX: ['100%', '200%'],
              transition: {
                duration: 3,
                repeat: Infinity,
                ease: "linear",
                delay: 1.5
              }
            }}
          />
        </motion.div>
      </div>
    </Container>
  );
};

export default BillingSkeleton; 